import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { HardHat, Loader2 } from "lucide-react";
import { useAuth, formatApiError } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const HERO =
  "https://images.unsplash.com/photo-1778074762033-c6595907684d?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHw0fHxlbmdpbmVlciUyMGhhcmRoYXQlMjB0YWJsZXR8ZW58MHx8fHwxNzgzODA3MTUzfDA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      navigate("/app");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-background">
      {/* Form side */}
      <div className="flex items-center justify-center p-6 sm:p-12">
        <div className="w-full max-w-md fade-in">
          <div className="flex items-center gap-3 mb-10">
            <div className="h-12 w-12 grid place-items-center bg-primary rounded-md">
              <HardHat className="h-7 w-7 text-primary-foreground" strokeWidth={2.5} />
            </div>
            <div>
              <div className="font-display text-xl font-black tracking-tight leading-none">
                CONSTRUCTVISION<span className="text-primary">-3D</span>
              </div>
              <div className="label-caps mt-1">Command Center</div>
            </div>
          </div>

          <h1 className="font-display text-3xl font-black tracking-tight mb-2">Sign in</h1>
          <p className="text-muted-foreground mb-8">Access your construction workspace.</p>

          <form onSubmit={submit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="label-caps">Email</Label>
              <Input
                id="email" type="email" required value={email}
                data-testid="login-email-input"
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com" className="h-12 bg-card"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password" className="label-caps">Password</Label>
              <Input
                id="password" type="password" required value={password}
                data-testid="login-password-input"
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••" className="h-12 bg-card"
              />
            </div>
            <Button
              type="submit" disabled={loading} data-testid="login-submit-button"
              className="w-full h-12 bg-primary text-primary-foreground hover:bg-primary/90 font-bold text-base"
            >
              {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : "Sign In"}
            </Button>
          </form>

          <p className="text-muted-foreground mt-8 text-sm">
            No account?{" "}
            <Link to="/register" data-testid="go-register-link" className="text-primary font-semibold hover:underline">
              Create workspace access
            </Link>
          </p>

          <div className="mt-8 p-4 border border-border rounded-md bg-card/50 text-xs text-muted-foreground">
            <span className="label-caps">Demo Admin</span>
            <div className="font-data mt-2 text-foreground">admin@constructvision.com / Admin@12345</div>
          </div>
        </div>
      </div>

      {/* Visual side */}
      <div className="hidden lg:block relative border-l border-border">
        <img src={HERO} alt="Engineers reviewing plans" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-background/70" />
        <div className="absolute bottom-0 left-0 right-0 p-12">
          <div className="font-display text-4xl font-black tracking-tight leading-tight max-w-md">
            Build with <span className="text-primary">precision</span>. Manage from the ground up.
          </div>
          <p className="text-muted-foreground mt-4 max-w-md">
            Real-time task matrices, version-controlled blueprints, and financial telemetry for every job site.
          </p>
        </div>
      </div>
    </div>
  );
}
